import {
  Accordion,
  Badge,
  Checkbox,
  Group,
  Stack,
  Text
} from '@mantine/core'
import { useMemo } from 'react'

export type FeaturePermissionItem = {
  id: string
  name: string
  description?: string | null
  disabled?: boolean
}

export type FeaturePermissionGroup = {
  featureKey: string
  featureName: string
  permissions: FeaturePermissionItem[]
}

export type AssignablePermissionLike = FeaturePermissionItem & {
  feature_key?: string
  feature_name?: string
  feature_sort_order?: number
}

export function groupPermissionsByFeature(
  permissions: AssignablePermissionLike[]
): FeaturePermissionGroup[] {
  const map = new Map<
    string,
    { featureName: string; sortOrder: number; permissions: FeaturePermissionItem[] }
  >()

  for (const perm of permissions) {
    const featureKey =
      perm.feature_key ?? perm.name.split('.')[0] ?? 'other'
    const featureName = perm.feature_name ?? featureKey.replace(/_/g, ' ')
    const sortOrder = perm.feature_sort_order ?? 9999
    const entry = map.get(featureKey) ?? {
      featureName,
      sortOrder,
      permissions: []
    }
    entry.permissions.push(perm)
    map.set(featureKey, entry)
  }

  return [...map.entries()]
    .sort(
      ([keyA, a], [keyB, b]) =>
        a.sortOrder - b.sortOrder || keyA.localeCompare(keyB)
    )
    .map(([featureKey, { featureName, permissions }]) => ({
      featureKey,
      featureName,
      permissions: permissions.sort((a, b) => a.name.localeCompare(b.name))
    }))
}

export type FeaturePermissionCheckboxGroupsProps = {
  groups: FeaturePermissionGroup[]
  value: string[]
  onChange: (ids: string[]) => void
  readOnly?: boolean
}

export function FeaturePermissionCheckboxGroups({
  groups,
  value,
  onChange,
  readOnly = false
}: FeaturePermissionCheckboxGroupsProps) {
  const valueSet = useMemo(() => new Set(value), [value])

  const toggleFeatureGroup = (
    featureKey: string,
    permissions: FeaturePermissionItem[],
    selectAll: boolean
  ) => {
    const ids = permissions.map((p) => p.id)
    if (selectAll) {
      onChange([...new Set([...value, ...ids])])
      return
    }
    const remove = new Set(ids)
    onChange(value.filter((id) => !remove.has(id)))
  }

  const isFeatureFullySelected = (permissions: FeaturePermissionItem[]) =>
    permissions.length > 0 &&
    permissions.every((p) => valueSet.has(p.id))

  const isFeaturePartiallySelected = (permissions: FeaturePermissionItem[]) => {
    const selected = permissions.filter((p) => valueSet.has(p.id)).length
    return selected > 0 && selected < permissions.length
  }

  if (groups.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No permissions available.
      </Text>
    )
  }

  return (
    <Accordion variant="separated" multiple>
      {groups.map(({ featureKey, featureName, permissions }) => {
        const groupIds = permissions.map((p) => p.id)
        const displayedValue = value.filter((id) => groupIds.includes(id))

        return (
          <Accordion.Item key={featureKey} value={featureKey}>
            <Accordion.Control>
              <Group justify="space-between" wrap="nowrap" pr="sm">
                <Stack gap={0}>
                  <Text size="sm" fw={600}>
                    {featureName}
                  </Text>
                  <Text size="xs" c="dimmed" ff="monospace">
                    {featureKey}
                  </Text>
                </Stack>
                {!readOnly && (
                  <Checkbox
                    checked={isFeatureFullySelected(permissions)}
                    indeterminate={isFeaturePartiallySelected(permissions)}
                    onChange={(e) =>
                      toggleFeatureGroup(
                        featureKey,
                        permissions,
                        e.currentTarget.checked
                      )
                    }
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select all permissions for ${featureName}`}
                  />
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              {readOnly ? (
                <Stack gap={4}>
                  {permissions.map((p) => (
                    <Text key={p.id} size="sm" ff="monospace">
                      {p.name}
                    </Text>
                  ))}
                </Stack>
              ) : (
                <Checkbox.Group
                  value={displayedValue}
                  onChange={(next) => {
                    const other = value.filter((id) => !groupIds.includes(id))
                    onChange([...other, ...next])
                  }}
                >
                  <Stack gap={4}>
                    {permissions.map((p) => (
                      <Checkbox
                        key={p.id}
                        value={p.id}
                        label={p.name}
                        description={p.description ?? undefined}
                        classNames={{
                          root: 'mb-1 group',
                          description: 'text-gray-300 mt-0 text-gray-500'
                        }}
                      />
                    ))}
                  </Stack>
                </Checkbox.Group>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        )
      })}
    </Accordion>
  )
}

export function FeaturePermissionCheckboxGroupsWithDeprecated({
  groups,
  value,
  onChange,
  readOnly
}: FeaturePermissionCheckboxGroupsProps) {
  return (
    <Stack gap="xs">
      {groups.map((group) =>
        group.permissions.some((p) => p.disabled) ? (
          <Badge key={group.featureKey} size="xs" variant="light" color="gray">
            {group.featureName}: deprecated permissions hidden
          </Badge>
        ) : null
      )}
      <FeaturePermissionCheckboxGroups
        groups={groups.map((g) => ({
          ...g,
          permissions: g.permissions.filter((p) => !p.disabled)
        }))}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    </Stack>
  )
}
